import React from "react";
import { type FlightEditState } from "../model";
import FlightSegmentAddPage from "./pages/addnewsegments";
import FlightDetailsEditPage from "./pages/editexistingflightdetails";
import FlightDetailPage from "./pages/flightdetail";
import InitialFlightCreatePage from "./pages/initialflightcreate";
import FlightSegmentEditPage from "./pages/segmentedit";

type ViewState = "editingFlight" | "editingSegment" | "addingNewSegment" | "detail" | "creatingNewFlight";

interface State {
  showApp: boolean

  flight: server.dto.Flight
  frequentFlyerPrograms: server.dto.FrequentFlyerProgram[]
  viewState: ViewState

  selectedSegment: server.dto.FlightSegment
  retrievedSegments: server.dto.FlightSegment[]

  shouldRefreshOnClose: boolean

  initialDate?: Date
  initialArtist?: server.dto.ArtistSmall
  // This is used to link
  fromPlannerItem?: server.dto.FromItem

  lastCreateState: FlightEditState
}

export default class FlightPlanningApp extends React.Component<any, State> {
  state: State = {
    showApp: false,

    flight: null,
    viewState: "creatingNewFlight",

    frequentFlyerPrograms: [],
    selectedSegment: null,
    retrievedSegments: [],
    shouldRefreshOnClose: false,

    lastCreateState: undefined
  };

  componentDidMount() {
    this.export();

    window.Api.Flights.GetFrequentFlyerPrograms()
      .then(res => { this.setState({ frequentFlyerPrograms: res.data }); });
  }

  loadFromSegmentId = async (segmentId: number) => {
    const result = await window.Api.Flights.GetFlightBySegment(segmentId);

    this.setState({
      flight: result.data,
      showApp: true,
      viewState: "detail"
    });
  };

  loadFlightAndEditSegment = async (segmentId: number) => {
    const result = await window.Api.Flights.GetFlightBySegment(segmentId);
    const segment = result.data.segments.find(seg => seg.id === segmentId);

    this.onExistingSegmentEdited(segment);
    this.setState({
      flight: result.data,
      showApp: true,
      viewState: "editingSegment"
    });
  };

  loadFlightAndEdit = async (flightId: number) => {
    const result = await window.Api.Flights.GetFlight(flightId);
    this.setState({
      flight: result.data,
      showApp: true,
      viewState: "editingFlight"
    });
  };

  loadFlightAndEditRetrievedSegments = async (flightId: number, segments: server.dto.FlightSegment[]) => {
    const result = await window.Api.Flights.GetFlight(flightId);
    this.onExistingFlightsAdded(segments);
    this.setState({
      flight: result.data,
      showApp: true
    });
  };

  loadFlightAndManualEntry = async (flightId: number) => {
    const result = await window.Api.Flights.GetFlight(flightId);
    this.onManualEntry();
    this.setState({
      flight: result.data,
      showApp: true
    });
  };

  createNewFlight = (lookupDate: Date, artist: server.dto.ArtistSmall, fromItem: server.dto.FromItem) => {
    this.setState({
      viewState: "creatingNewFlight",
      showApp: true,
      initialDate: lookupDate,
      initialArtist: artist,
      fromPlannerItem: fromItem
    });
  };

  onExistingFlightsAdded = (segments: server.dto.FlightSegment[]) => {
    this.setState({ retrievedSegments: segments, selectedSegment: null, viewState: "addingNewSegment" });
  };

  onManualEntry = () => {
    this.setState({ retrievedSegments: [], selectedSegment: null, viewState: "addingNewSegment" });
  };

  onExistingSegmentEdited = (segment: server.dto.FlightSegment) => {
    this.setState({ retrievedSegments: [], selectedSegment: segment, viewState: "editingSegment" });
  };

  onSave = (newFlight: server.dto.Flight) => {
    const { fromPlannerItem } = this.state;
    if (fromPlannerItem && fromPlannerItem.type === "show" && newFlight.segments.length > 0) {
      let lastAddedSegments;

      if (this.state.flight) {
        lastAddedSegments = newFlight.segments
          .filter(newSeg => this.state.flight.segments.find(oldSeg => oldSeg.id === newSeg.id) == null);
      } else {
        // This means the flight was just created. So the segments are all belonging to this show
        lastAddedSegments = newFlight.segments;
      }

      if (lastAddedSegments.length > 0) {
        for (const seg of lastAddedSegments) {
          // No need to await as we don't show the result anyway
          window.CoreApi.Show.LinkFlightSegment({ showId: fromPlannerItem.id, flightSegmentId: seg.id });
        }
      }
    }

    this.setState({ shouldRefreshOnClose: true, flight: newFlight, selectedSegment: null, retrievedSegments: [], viewState: "detail" });
  };

  onFlightEditClicked = () => {
    this.setState({ viewState: "editingFlight" });
  };

  onFilesUploaded = async () => {
    const result = await window.Api.Flights.GetFlight(this.state.flight.id);
    this.setState({ flight: result.data });
  };

  onAppClose = (flightEditState?: FlightEditState) => {
    this.setState({
      showApp: false,
      fromPlannerItem: undefined,
      initialArtist: undefined,
      initialDate: undefined,
      lastCreateState: flightEditState
    }, () => {
      if (this.state.shouldRefreshOnClose) {
        window.location.reload();
      }
    });
  };

  public render(): JSX.Element {
    const { viewState } = this.state;

    if (!this.state.showApp) {
      return null;
    }

    return <>
      {viewState === "creatingNewFlight" && <InitialFlightCreatePage onModalClose={this.onAppClose} frequentFlyerPrograms={this.state.frequentFlyerPrograms} onFlightCreated={this.onSave} initialArtist={this.state.initialArtist} initialDate={this.state.initialDate} lastFlightState={this.state.lastCreateState} />}
      {viewState === "detail" && <FlightDetailPage onModalClose={this.onAppClose} onFilesUploaded={this.onFilesUploaded} flight={this.state.flight} onExistingFlightAdded={this.onExistingFlightsAdded} onManualEntryClicked={this.onManualEntry} onSegmentEdit={this.onExistingSegmentEdited} onSegmentDelete={this.onSave} onFlightEdit={this.onFlightEditClicked} />}
      {viewState === "editingSegment" && <FlightSegmentEditPage onModalClose={this.onAppClose} frequentFlyerPrograms={this.state.frequentFlyerPrograms} flight={this.state.flight} editedSegment={this.state.selectedSegment} onSave={this.onSave} />}
      {viewState === "editingFlight" && <FlightDetailsEditPage onModalClose={this.onAppClose} initialFlight={this.state.flight} onExistingFlightAdded={this.onExistingFlightsAdded} onManualEntryClicked={this.onManualEntry} onFlightDetailsSaved={this.onSave} />}
      {viewState === "addingNewSegment" && <FlightSegmentAddPage onModalClose={this.onAppClose} frequentFlyerPrograms={this.state.frequentFlyerPrograms} flight={this.state.flight} initialSegments={this.state.retrievedSegments} onSave={this.onSave} />}
    </>;
  }

  export = () => {
    SystemOneLibrary.exports = {
      ...SystemOneLibrary.exports,
      newFlight: (lookupDate: Date, artist: server.dto.ArtistSmall, fromItem: server.dto.FromItem) => {
        // if (store) {
        //     store.dispatch(newFlight(lookupDate, artist, fromItem) as any);
        // }
        // If created from 'new', no params
        // If created by clicking a date in the planner, include date
        this.createNewFlight(lookupDate, artist, fromItem);
      },
      openFlightEditModal: (segmentId: number) => {
        this.loadFromSegmentId(segmentId);
      },
      editSegment: (segmentId: number) => {
        this.loadFlightAndEditSegment(segmentId);
      },
      editFlight: (flightId: number) => {
        this.loadFlightAndEdit(flightId);
      },
      manualFlightEntry: (flightId: number) => {
        this.loadFlightAndManualEntry(flightId);
      },
      existingSegmentsAdded: (flightId: number, segments: server.dto.FlightSegment[]) => {
        this.loadFlightAndEditRetrievedSegments(flightId, segments);
      }
    };
  };
}
